import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, Target, Flame, BookOpen, Clock, Star, Award, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: any;
  unlocked: boolean;
  progress: number;
  target: number;
  category: string;
}

const DashboardAchievements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [stats, setStats] = useState({
    totalUnlocked: 0,
    totalAchievements: 0
  });

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: progress } = await supabase
        .from("course_progress")
        .select("*")
        .eq("user_id", user.id);

      if (progress) {
        const completed = progress.filter(p => p.completed).length;
        const totalTime = completed * 45;
        const streak = calculateStreak(progress);

        const achievementsList: Achievement[] = [
          {
            id: "first_step",
            title: "First Step",
            description: "Complete your first module",
            icon: BookOpen,
            unlocked: completed >= 1,
            progress: Math.min(completed, 1),
            target: 1,
            category: "Learning"
          },
          {
            id: "getting_started",
            title: "Getting Started",
            description: "Complete 5 modules",
            icon: Target,
            unlocked: completed >= 5,
            progress: Math.min(completed, 5),
            target: 5,
            category: "Learning"
          },
          {
            id: "dedicated_learner",
            title: "Dedicated Learner",
            description: "Complete 10 modules",
            icon: Award,
            unlocked: completed >= 10,
            progress: Math.min(completed, 10),
            target: 10,
            category: "Learning"
          },
          {
            id: "master_student",
            title: "Master Student",
            description: "Complete 25 modules",
            icon: Trophy,
            unlocked: completed >= 25,
            progress: Math.min(completed, 25),
            target: 25,
            category: "Learning"
          },
          {
            id: "time_invested",
            title: "Time Invested",
            description: "Study for 5 hours total",
            icon: Clock,
            unlocked: totalTime >= 300,
            progress: Math.min(totalTime, 300),
            target: 300,
            category: "Dedication"
          },
          {
            id: "marathon_learner",
            title: "Marathon Learner",
            description: "Study for 20 hours total",
            icon: Zap,
            unlocked: totalTime >= 1200,
            progress: Math.min(totalTime, 1200),
            target: 1200,
            category: "Dedication"
          },
          {
            id: "hot_streak",
            title: "Hot Streak",
            description: "Maintain a 3-day streak",
            icon: Flame,
            unlocked: streak >= 3,
            progress: Math.min(streak, 3),
            target: 3,
            category: "Consistency"
          },
          {
            id: "unstoppable",
            title: "Unstoppable",
            description: "Maintain a 7-day streak",
            icon: Star,
            unlocked: streak >= 7,
            progress: Math.min(streak, 7),
            target: 7,
            category: "Consistency"
          }
        ];

        const unlockedCount = achievementsList.filter(a => a.unlocked).length;
        
        setAchievements(achievementsList);
        setStats({
          totalUnlocked: unlockedCount,
          totalAchievements: achievementsList.length
        });
      }
    } catch (error) {
      console.error("Error loading achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (progress: any[]) => {
    const completedDates = progress
      .filter(p => p.completed_at)
      .map(p => new Date(p.completed_at).toDateString())
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    for (let i = 0; i < completedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      
      if (completedDates[i] === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const groupedAchievements = achievements.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = [];
    }
    acc[achievement.category].push(achievement);
    return acc;
  }, {} as Record<string, Achievement[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Achievements</h1>
          <p className="text-muted-foreground">Unlock badges as you progress through your journey</p>
        </div>

        <Card>
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <p className="text-sm text-muted-foreground">Progress</p>
              <p className="text-3xl font-bold">
                {stats.totalUnlocked} / {stats.totalAchievements}
              </p>
            </div>
            <div className="text-right">
              <Trophy className="h-12 w-12 text-primary mb-2" />
              <Badge variant="secondary">
                {Math.round((stats.totalUnlocked / stats.totalAchievements) * 100)}% Complete
              </Badge>
            </div>
          </CardContent>
        </Card>

        {Object.entries(groupedAchievements).map(([category, categoryAchievements]) => (
          <div key={category} className="space-y-4">
            <h2 className="text-2xl font-bold">{category}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {categoryAchievements.map((achievement) => {
                const Icon = achievement.icon;
                return (
                  <Card
                    key={achievement.id}
                    className={`transition-all hover-scale ${
                      achievement.unlocked ? 'border-primary bg-accent/50' : 'opacity-60'
                    }`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-full ${achievement.unlocked ? 'bg-primary/20' : 'bg-muted'}`}>
                            <Icon className={`h-6 w-6 ${achievement.unlocked ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{achievement.title}</CardTitle>
                            <CardDescription>{achievement.description}</CardDescription>
                          </div>
                        </div>
                        {achievement.unlocked && (
                          <Badge className="bg-primary">Unlocked!</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">
                            {achievement.progress} / {achievement.target}
                          </span>
                        </div>
                        <Progress
                          value={(achievement.progress / achievement.target) * 100}
                          className="h-2"
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardAchievements;
